// ═══ JURNAL DIGESTIV (tab privat) ════════════════════════════════════════════
// Jurnal alimentar + scaun (scala Bristol) cu timestamp, plus analiză AI de
// corelații mâncare ↔ simptome/scaun (FODMAP etc.).
// Simptomele NU se dublează aici: se afișează read-only din store-ul existent
// (tab-ul Tratament), iar butonul „+ Simptom" deschide același modal ca acolo.
import React, { useState } from "react";
import { t, aiIn, aiTextLang } from './i18n';
import { fmConfirm } from './dialog';
import { fmtD, todayISO, addDaysISO, toText, asArr } from './helpers';
import { HeartSpinner, IconTrash, TimeInput, DateField, Dropdown } from './ui';
import bristol1 from './assets/bristol/bristol1.png';
import bristol2 from './assets/bristol/bristol2.png';
import bristol3 from './assets/bristol/bristol3.png';
import bristol4 from './assets/bristol/bristol4.png';
import bristol5 from './assets/bristol/bristol5.png';
import bristol6 from './assets/bristol/bristol6.png';
import bristol7 from './assets/bristol/bristol7.png';


// Scala Bristol — 1-7, cu etichete scurte
export const BRISTOL = [
  {v:1, l:"Tip 1 — bile tari, separate (constipație severă)"},
  {v:2, l:"Tip 2 — formă de cârnat, cocoloșat (constipație)"},
  {v:3, l:"Tip 3 — cârnat cu crăpături (normal)"},
  {v:4, l:"Tip 4 — neted și moale (ideal)"},
  {v:5, l:"Tip 5 — bucăți moi, margini clare (tendință spre diaree)"},
  {v:6, l:"Tip 6 — bucăți pufoase, margini zdrențuite (diaree ușoară)"},
  {v:7, l:"Tip 7 — apos, fără bucăți solide (diaree)"},
];

// Ilustrațiile scalei Bristol — imagini reale din planșa medicală clasică
// (decupate, cu fundal transparent), câte una pentru fiecare tip 1-7.
const BRISTOL_IMGS = {1:bristol1,2:bristol2,3:bristol3,4:bristol4,5:bristol5,6:bristol6,7:bristol7};
export function BristolIcon({type, size=48}) {
  const src = BRISTOL_IMGS[+type];
  if(!src) return null;
  return <img src={src} alt="" draggable={false}
    style={{width:size, height:"auto", maxHeight:size*0.6, objectFit:"contain", display:"block", flexShrink:0, userSelect:"none"}}/>;
}

const nowHM = () => new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false});

export function JurnalTab({gutData, setGutData, symptoms, setModal, callAI}) {
  const data = gutData || {};
  const entries = data.entries || [];
  const analysis = data.analysis || null;

  const [day, setDay] = useState(()=>todayISO());
  const [adding, setAdding] = useState(null); // null | "food" | "stool"
  const [editingId, setEditingId] = useState(null); // id-ul intrării în curs de editare
  const [foodText, setFoodText] = useState("");
  const [foodTime, setFoodTime] = useState(nowHM());
  const [bristol, setBristol] = useState(4);
  const [stoolNote, setStoolNote] = useState("");
  const [stoolTime, setStoolTime] = useState(nowHM());
  const [analyzing, setAnalyzing] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [view, setView] = useState("day"); // "day" | "ai"
  // Selectorul de perioadă pentru analiza AI: 30 zile / an / interval custom
  const [periodMode, setPeriodMode] = useState("30"); // "30" | "year" | "custom"
  const [selYear, setSelYear] = useState(()=>new Date().getFullYear());
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Anii pentru care există date în jurnal (+ anul curent)
  const availableYears = (()=>{
    const s = new Set([new Date().getFullYear()]);
    entries.forEach(e=>{ if(e.date) s.add(+e.date.slice(0,4)); });
    (symptoms||[]).forEach(x=>{ if(x.date) s.add(+x.date.slice(0,4)); });
    return [...s].sort((a,b)=>b-a);
  })();

  // Calculează {from, to, label} din selecția curentă
  function getPeriod(){
    const today = todayISO();
    if(periodMode==="year"){
      const from = `${selYear}-01-01`;
      const to = (+selYear===new Date().getFullYear()) ? today : `${selYear}-12-31`;
      return { from, to, label:`${t("Anul")} ${selYear}` };
    }
    if(periodMode==="custom"){
      if(!customStart||!customEnd) return null;
      const a = customStart<=customEnd ? customStart : customEnd;
      const b = customStart<=customEnd ? customEnd : customStart;
      return { from:a, to:b, label:`${fmtD(a)} – ${fmtD(b)}` };
    }
    const from = addDaysISO(today, -30);
    return { from, to:today, label:t("Ultimele 30 zile") };
  }

  const dayEntries = entries.filter(e=>e.date===day).sort((a,b)=>(a.time||"").localeCompare(b.time||""));
  const daySymptoms = (symptoms||[]).filter(s=>s.date===day);
  const isToday = day===todayISO();

  function addEntry(entry){
    setGutData(prev=>({ ...(prev||{}), entries:[...((prev||{}).entries||[]), {id:Date.now(), date:day, ...entry}] }));
  }
  function updateEntry(id, patch){
    setGutData(prev=>({ ...(prev||{}), entries:((prev||{}).entries||[]).map(e=>e.id===id?{...e,...patch}:e) }));
  }
  function deleteEntry(id){
    setGutData(prev=>({ ...(prev||{}), entries:((prev||{}).entries||[]).filter(e=>e.id!==id) }));
  }
  function startEdit(e){
    setEditingId(e.id);
    if(e.type==="food"){ setFoodText(e.text||""); setFoodTime(e.time||nowHM()); setAdding("food"); }
    else { setBristol(e.bristol||4); setStoolNote(e.note||""); setStoolTime(e.time||nowHM()); setAdding("stool"); }
  }
  function resetForms(){
    setFoodText(""); setFoodTime(nowHM());
    setStoolNote(""); setBristol(4); setStoolTime(nowHM());
    setAdding(null); setEditingId(null);
  }
  function saveFood(){
    if(!foodText.trim()) return;
    if(editingId) updateEntry(editingId, {time:foodTime, text:foodText.trim()});
    else addEntry({type:"food", time:foodTime, text:foodText.trim()});
    resetForms();
  }
  function saveStool(){
    if(editingId) updateEntry(editingId, {time:stoolTime, bristol, note:stoolNote.trim()});
    else addEntry({type:"stool", time:stoolTime, bristol, note:stoolNote.trim()});
    resetForms();
  }

  async function runAnalysis(){
    const period = getPeriod();
    if(!period){ setAiError(t("Alege un interval valid (de la / până la).")); return; }
    setAnalyzing(true); setAiError(null);
    const { from, to, label } = period;
    const inRange = d => d>=from && d<=to;
    const foods = entries.filter(e=>e.type==="food"&&inRange(e.date)).map(e=>({data:e.date, ora:e.time, a_mancat:e.text}));
    const stools = entries.filter(e=>e.type==="stool"&&inRange(e.date)).map(e=>({data:e.date, ora:e.time, bristol:e.bristol, nota:e.note||undefined}));
    const sympt = (symptoms||[]).filter(s=>inRange(s.date)).map(s=>({data:s.date, ora:s.time, simptome:s.items, nota:s.note||undefined}));
    if(foods.length===0){ setAnalyzing(false); setAiError(t("Nu există mese înregistrate în perioada aleasă.")); return; }
    const prompt = `Ești un asistent specializat în sănătate digestivă. Analizează jurnalul alimentar al unui pacient cu probleme digestive (balonare frecventă) și caută CORELAȚII între alimente/băuturi și simptome sau consistența scaunului. Răspunde ${aiIn()}.

MESE ȘI BĂUTURI (${label}): ${JSON.stringify(foods)}
SCAUNE (scala Bristol 1-7; 3-4 = normal, 1-2 = constipație, 5-7 = diaree): ${JSON.stringify(stools)}
SIMPTOME RAPORTATE: ${JSON.stringify(sympt)}

Instrucțiuni:
- Caută pattern-uri temporale: aliment consumat → simptom/scaun modificat în următoarele 4-48 de ore.
- Marchează alimentele bogate în FODMAP (fructani, lactoză, fructoză, polioli, GOS) și spune CARE categorie FODMAP e suspectă.
- Ține cont și de combinații (ex. lactate + gluten în aceeași masă) și de cantități repetate.
- Fii onest despre nivelul de încredere: cu date puține, spune că e doar o ipoteză.
- NU pune diagnostice. Sugerează teste de eliminare simple și discuția cu un medic gastroenterolog unde e cazul.

Returnează DOAR acest JSON:
{"overview":"privire generală 2-3 propoziții despre ce arată jurnalul",
"suspects":[{"food":"alimentul suspect","reason":"de ce e suspect + în ce categorie FODMAP intră dacă e cazul","confidence":"ridicat|mediu|scăzut"}],
"stoolPattern":"ce arată evoluția scaunului în perioadă",
"correlations":["corelație concretă observată aliment → simptom/scaun"],
"safe":["aliment consumat des FĂRĂ simptome asociate"],
"recommendations":["recomandare concretă (ex. test de eliminare 2 săptămâni)"]}
${aiTextLang()}`;
    const res = await callAI(prompt, 8000);
    setAnalyzing(false);
    if(res && !res._error){
      const a = {...res, generatedAt:new Date().toLocaleString("ro-RO"), period:label, periodRange:`${fmtD(from)} – ${fmtD(to)}`};
      setGutData(prev=>({ ...(prev||{}), analysis:a }));
    } else {
      setAiError(window._lastAIError || t("Analiza a eșuat. Încearcă din nou."));
    }
  }

  const confLabel = c => /ridicat|high/i.test(c||"") ? t("încredere ridicată") : /mediu|medium/i.test(c||"") ? t("încredere medie") : t("încredere scăzută");
  const confColor = c => /ridicat|high/i.test(c||"") ? "var(--red)" : /mediu|medium/i.test(c||"") ? "var(--amber)" : "var(--text-3)";

  return (
    <div>
      {/* Sub-taburi: Jurnal zilnic / Analiză AI */}
      <div className="subtabs">
        {[["day","📔 "+t("Jurnal zilnic")],["ai","✦ "+t("Analiză AI")]].map(([k,l])=>(
          <button key={k} className={`stab${view===k?" act":""}`} onClick={()=>setView(k)}>{l}</button>
        ))}
      </div>

      {view==="day"&&(
        <div>
          {/* Navigare zi */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button className="bg" style={{padding:"4px 14px",fontSize:15}} onClick={()=>{resetForms();setDay(d=>addDaysISO(d,-1));}}>‹</button>
            <div style={{textAlign:"center"}}>
              <div style={{fontWeight:700,fontSize:15}}>{isToday?t("Astăzi"):fmtD(day)}</div>
              {!isToday&&<button onClick={()=>{resetForms();setDay(todayISO());}} style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:11,fontFamily:"inherit",padding:0}}>{t("↩ Sari la azi")}</button>}
            </div>
            <button className="bg" style={{padding:"4px 14px",fontSize:15,opacity:isToday?0.4:1}} disabled={isToday} onClick={()=>{resetForms();setDay(d=>addDaysISO(d,1));}}>›</button>
          </div>

          {/* Butoane adăugare */}
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            <button className={adding==="food"?"bp":"bg"} style={{flex:1,padding:"10px",fontSize:13}} onClick={()=>{ if(adding==="food") resetForms(); else { resetForms(); setAdding("food"); } }}>🍽️ {t("Mâncare / Băutură")}</button>
            <button className={adding==="stool"?"bp":"bg"} style={{flex:1,padding:"10px",fontSize:13}} onClick={()=>{ if(adding==="stool") resetForms(); else { resetForms(); setAdding("stool"); } }}>🚻 {t("Scaun")}</button>
            <button className="bg" style={{flex:1,padding:"10px",fontSize:13}} onClick={()=>setModal({type:"addSymptom"})}>🤒 {t("Simptom")}</button>
          </div>

          {/* Formular mâncare/băutură */}
          {adding==="food"&&(
            <div className="card" style={{marginBottom:14}}>
              {editingId&&<div style={{fontSize:11,color:"var(--blue)",marginBottom:6}}>✎ {t("Editezi o intrare existentă")}</div>}
              <div className="lbl">{t("Ce ai mâncat / băut?")}</div>
              <textarea value={foodText} onChange={e=>setFoodText(e.target.value)} rows={2} autoFocus
                placeholder={t("ex: iaurt cu musli și miere, cafea cu lapte")}
                style={{width:"100%",boxSizing:"border-box",marginBottom:10}}/>
              <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
                <div style={{flex:1}}><div className="lbl">{t("Ora")}</div><TimeInput value={foodTime} onChange={setFoodTime}/></div>
                <button className="bp" style={{padding:"9px 20px"}} disabled={!foodText.trim()} onClick={saveFood}>{editingId?t("Salvează"):t("Adaugă")}</button>
              </div>
            </div>
          )}

          {/* Formular scaun */}
          {adding==="stool"&&(
            <div className="card" style={{marginBottom:14}}>
              {editingId&&<div style={{fontSize:11,color:"var(--blue)",marginBottom:6}}>✎ {t("Editezi o intrare existentă")}</div>}
              <div className="lbl" style={{marginBottom:8}}>{t("Consistență (scala Bristol)")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
                {BRISTOL.map(b=>(
                  <button key={b.v} onClick={()=>setBristol(b.v)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",textAlign:"left",fontFamily:"inherit",fontSize:12.5,
                      background:bristol===b.v?"var(--blue-bg)":"var(--bg)",
                      border:`1px solid ${bristol===b.v?"#7c3aed":"var(--border-2)"}`,
                      color:bristol===b.v?"var(--blue-2)":"var(--text-2)"}}>
                    <BristolIcon type={b.v} size={44}/>{t(b.l)}
                  </button>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                <div><div className="lbl">{t("Ora")}</div><TimeInput value={stoolTime} onChange={setStoolTime}/></div>
                <div><div className="lbl">{t("Notă (opțional)")}</div><input value={stoolNote} onChange={e=>setStoolNote(e.target.value)} placeholder={t("ex: urgență, mucus, durere")}/></div>
              </div>
              <button className="bp" style={{width:"100%"}} onClick={saveStool}>{editingId?t("Salvează"):t("Adaugă")}</button>
            </div>
          )}

          {/* Cronologia zilei: mese + scaune + simptome (read-only, din Tratament) */}
          {(()=>{
            const merged = [
              ...dayEntries.map(e=>({kind:e.type, time:e.time||"", e})),
              ...daySymptoms.map(s=>({kind:"symptom", time:s.time||"", s})),
            ].sort((a,b)=>a.time.localeCompare(b.time));
            if(merged.length===0) return (
              <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"36px 16px"}}>
                <div style={{fontSize:36,marginBottom:8}}>📔</div>
                {t("Nimic înregistrat în această zi. Adaugă ce ai mâncat, băut sau cum a fost scaunul.")}
              </div>
            );
            return (
              <div className="card" style={{padding:"4px 14px"}}>
                {merged.map((m,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:i<merged.length-1?"1px solid var(--border)":"none"}}>
                    <div style={{fontFamily:"monospace",fontSize:12,color:"var(--text-3)",minWidth:42,paddingTop:2}}>{m.time||"—"}</div>
                    <div style={{fontSize:16,flexShrink:0}}>{m.kind==="food"?"🍽️":m.kind==="stool"?"🚻":"🤒"}</div>
                    <div style={{flex:1,minWidth:0}}>
                      {m.kind==="food"&&<div style={{fontSize:13,lineHeight:1.5}}>{m.e.text}</div>}
                      {m.kind==="stool"&&(
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <BristolIcon type={+m.e.bristol} size={36}/>
                            <span style={{fontSize:13,fontWeight:600}}>{t("Tip")} {m.e.bristol}</span>
                          </div>
                          <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{t(BRISTOL.find(b=>b.v===+m.e.bristol)?.l||"")}</div>
                          {m.e.note&&<div style={{fontSize:12,color:"var(--text-2)",marginTop:2}}>📝 {m.e.note}</div>}
                        </div>
                      )}
                      {m.kind==="symptom"&&(
                        <div>
                          <div style={{fontSize:13}}>{(m.s.items||[]).map(x=>t(x)).join(", ")}</div>
                          {m.s.note&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{m.s.note}</div>}
                          <div style={{fontSize:10,color:"var(--text-4)",marginTop:2}}>{t("din Tratament → Simptome")}</div>
                        </div>
                      )}
                    </div>
                    {m.kind!=="symptom"&&(
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        <button className="bg" style={{fontSize:11,padding:"4px 8px"}} onClick={()=>startEdit(m.e)}>✎</button>
                        <button className="bdel" onClick={()=>fmConfirm(t("Ștergi această intrare?"),{danger:true,okText:"Șterge"}).then(ok=>{if(ok)deleteEntry(m.e.id);})}><IconTrash size={14}/></button>
                      </div>
                    )}
                    {m.kind==="symptom"&&(
                      <button className="bg" style={{fontSize:11,padding:"4px 8px",flexShrink:0}} onClick={()=>setModal({type:"editSymptom",data:m.s})}>✎</button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {view==="ai"&&(
        <div>
          <div style={{marginBottom:12,padding:"9px 12px",background:"var(--amber-bg-soft)",border:"1px solid var(--amber-line)",borderRadius:10,fontSize:11,color:"#c4a355"}}>
            ⓘ {t("Analiză generată de AI pe baza jurnalului — orientativă, nu diagnostic. Corelațiile devin utile după cel puțin 1-2 săptămâni de date.")}
          </div>

          {/* Selector perioadă — ca la Sumar AI */}
          <div className="card" style={{marginBottom:12}}>
            <div className="lbl" style={{marginBottom:10}}>{t("Perioadă analizată")}</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom: periodMode==="30"?0:12}}>
              {[["30",t("Ultimele 30 zile")],["year",t("An")],["custom",t("Interval")]].map(([v,l])=>(
                <button key={v} onClick={()=>setPeriodMode(v)} style={{
                  background:periodMode===v?"#7c3aed":"var(--border)",
                  border:`1px solid ${periodMode===v?"#7c3aed":"var(--border-2)"}`,
                  color:periodMode===v?"#fff":"var(--text-2)",
                  borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:12.5,fontFamily:"inherit"}}>{l}</button>
              ))}
            </div>
            {periodMode==="year"&&(
              <div style={{width:130}}><Dropdown value={selYear} onChange={v=>setSelYear(+v)} options={availableYears.map(y=>({v:y,l:String(y)}))}/></div>
            )}
            {periodMode==="custom"&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><div className="lbl" style={{fontSize:10}}>{t("De la")}</div><DateField value={customStart} onChange={v=>setCustomStart(v)}/></div>
                <div><div className="lbl" style={{fontSize:10}}>{t("Până la")}</div><DateField value={customEnd} onChange={v=>setCustomEnd(v)}/></div>
              </div>
            )}
          </div>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:8,flexWrap:"wrap"}}>
            <div style={{fontSize:12,color:"var(--text-3)"}}>
              {entries.length} {t("intrări în jurnal")} · {(symptoms||[]).length} {t("simptome înregistrate")}
            </div>
            <button className="bai" onClick={runAnalysis} disabled={analyzing||entries.length===0}>
              ✦ {analyzing?t("Analizez..."):t("Analizează")}
            </button>
          </div>
          {analyzing&&<HeartSpinner label={t("Caut corelații între alimente, simptome și scaun...")}/>}
          {aiError&&!analyzing&&(
            <div style={{padding:"14px",background:"var(--red-bg-soft)",border:"1px solid var(--red)",borderRadius:10,fontSize:13,color:"var(--text-2)"}}>⚠ {aiError}</div>
          )}
          {!analysis&&!analyzing&&!aiError&&(
            <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"36px 16px"}}>
              <div style={{fontSize:36,marginBottom:8}}>✦</div>
              {t("Completează jurnalul câteva zile, apoi apasă „Analizează” — AI-ul caută ce alimente îți declanșează balonarea sau alte simptome.")}
            </div>
          )}
          {analysis&&!analyzing&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:10,color:"var(--text-4)",textAlign:"right"}}>{analysis.period}{analysis.periodRange?` (${analysis.periodRange})`:""} · {analysis.generatedAt}</div>
              {analysis.overview&&<div className="card" style={{margin:0,border:"1px solid var(--blue-line)"}}><div className="lbl" style={{color:"var(--blue)"}}>{t("Privire generală")}</div><div style={{fontSize:13,lineHeight:1.6,marginTop:4}}>{toText(analysis.overview)}</div></div>}
              {asArr(analysis.suspects).length>0&&(
                <div className="card" style={{margin:0,border:"1px solid var(--red-line)"}}>
                  <div className="lbl" style={{color:"var(--red)",marginBottom:6}}>{t("🚨 Alimente suspecte")}</div>
                  <div style={{fontSize:10.5,color:"var(--text-3)",lineHeight:1.5,marginBottom:8,paddingBottom:8,borderBottom:"1px dashed var(--border-2)"}}>
                    {t("Încrederea = cât de sigur e AI-ul că alimentul e cauza, nu o coincidență:")} <b style={{color:"var(--red)"}}>{t("ridicată")}</b> {t("= pattern repetat constant;")} <b style={{color:"var(--amber)"}}>{t("medie")}</b> {t("= apare des dar nu mereu;")} <b>{t("scăzută")}</b> {t("= o ipoteză din puține date.")}
                  </div>
                  {asArr(analysis.suspects).map((x,i)=>{
                    const o = typeof x==="object"&&x ? x : {food:toText(x)};
                    return (
                      <div key={i} style={{padding:"8px 0",borderBottom:i<asArr(analysis.suspects).length-1?"1px solid var(--border)":"none"}}>
                        <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,fontWeight:700}}>{toText(o.food)}</span>
                          {o.confidence&&<span className="pill" style={{fontSize:9,background:"var(--bg)",color:confColor(o.confidence)}}>{confLabel(o.confidence)}</span>}
                        </div>
                        {o.reason&&<div style={{fontSize:12,color:"var(--text-2)",lineHeight:1.5,marginTop:3}}>{toText(o.reason)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              {asArr(analysis.correlations).length>0&&<div className="card" style={{margin:0}}><div className="lbl" style={{color:"var(--amber)",marginBottom:6}}>{t("🔗 Corelații observate")}</div>{asArr(analysis.correlations).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
              {analysis.stoolPattern&&<div className="card" style={{margin:0}}><div className="lbl" style={{color:"var(--purple)",marginBottom:6}}>{t("🚻 Evoluția scaunului")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(analysis.stoolPattern)}</div></div>}
              {asArr(analysis.safe).length>0&&<div className="card" style={{margin:0,border:"1px solid var(--green-line)"}}><div className="lbl" style={{color:"var(--green)",marginBottom:6}}>{t("✓ Bine tolerate")}</div>{asArr(analysis.safe).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
              {asArr(analysis.recommendations).length>0&&<div className="card" style={{margin:0,border:"1px solid var(--purple-line)"}}><div className="lbl" style={{color:"var(--purple)",marginBottom:6}}>{t("Recomandări")}</div>{asArr(analysis.recommendations).map((x,i)=><div key={i} style={{fontSize:12,color:"var(--text-2)",marginTop:5,lineHeight:1.5}}>• {toText(x)}</div>)}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
